/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '../../../theme/theme';
import {IconSize} from '../../../types/types';
import {
  ALERT_INDICATOR_ICONS,
  ALERT_INDICATORS_BY_VEHICLE_TYPE,
  AlertIndicatorKey,
  getAlertIndicatorColor,
  IndicatorStatus,
  SEVERITY_AWARE_ALERT_INDICATORS,
  VehicleFuelType
} from './vehicleTypeIndicatorConfig';

export interface AlertIndicatorValue {
  value: string;
  status?: IndicatorStatus;
}

export type AlertIndicatorValues = Partial<Record<AlertIndicatorKey, AlertIndicatorValue>>;

interface AssetAlertIndicatorsProps {
  vehicleType: VehicleFuelType;
  values: AlertIndicatorValues;
}

const useStyles = makeStyles(() => ({
  alerts : {
    display    : 'flex',
    flexWrap   : 'nowrap',
    alignItems : 'center',
    columnGap  : '4px',
    flexShrink : 0
  },
  indicator : {
    display    : 'flex',
    alignItems : 'center',
    // matches the Health indicator chip's confirmed zero icon-label gap
    // (see AssetHealthIndicators) - Alerts reuses the same chip pattern ...
    gap        : 0,
    whiteSpace : 'nowrap'
  },
  label : {
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '10px',
    lineHeight    : '14px',
    letterSpacing : '0.5px',
    color         : dlsColor('grey2')
  }
}));

// only GPS Quality actually recolors by severity (see
// SEVERITY_AWARE_ALERT_INDICATORS); Engine Light/EV System are always
// Critical, and hardcode Status/Red directly rather than taking this prop ...
const resolveAlertStatus = (indicatorKey: AlertIndicatorKey, status?: IndicatorStatus): IndicatorStatus => (
  SEVERITY_AWARE_ALERT_INDICATORS.includes(indicatorKey) ? (status || 'default') : 'critical'
);

/**
 * Returns whether any indicator applicable to `vehicleType` has an active
 * value - used by `AssetSummaryCardDiagnostics` to decide whether to render
 * the Alerts section and its divider at all.
 */
export const hasActiveAlerts = (vehicleType: VehicleFuelType, values: AlertIndicatorValues): boolean => (
  ALERT_INDICATORS_BY_VEHICLE_TYPE[vehicleType].some(indicatorKey => values[indicatorKey])
);

/**
 * AssetAlertIndicators renders only the currently active Alerts applicable
 * to a given vehicle type, in the ION-7392 Vehicle Type Indicator Rules
 * order. Only GPS Quality's icon color varies (Warning "Poor" / Critical
 * "No GPS"); Engine Light and EV System are Critical-only and never change.
 * The caller (`AssetSummaryCardDiagnostics`) is responsible for hiding this
 * component and its divider entirely when `hasActiveAlerts` returns `false`.
 *
 * ```typescript
 *
 *   import {AssetAlertIndicators} from
 *     '@ion/dls/src/components/assetcard/current/AssetAlertIndicators';
 *   ...
 *
 *   <AssetAlertIndicators vehicleType="ev" values={{
 *     gpsQuality: {value: 'Poor', status: 'warning'}
 *   }} />
 *
 * ```
 *
 */
export const AssetAlertIndicators = ({vehicleType, values}: AssetAlertIndicatorsProps) => {
  const classes        = useStyles();
  const indicatorKeys = ALERT_INDICATORS_BY_VEHICLE_TYPE[vehicleType]
    .filter(indicatorKey => values[indicatorKey]);
  return (
    <div className={classes.alerts}>
      {indicatorKeys.map(indicatorKey => {
        const Icon           = ALERT_INDICATOR_ICONS[indicatorKey];
        const resolvedStatus = resolveAlertStatus(indicatorKey, values[indicatorKey].status);
        return (
          <div className={classes.indicator} key={indicatorKey}>
            <Icon size={IconSize.medium} color={getAlertIndicatorColor(resolvedStatus)} />
            <span className={classes.label}>
              {values[indicatorKey].value}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default AssetAlertIndicators;
